import { displayEventDates } from "@/lib/utils";
import { LanguageSelect } from "../LanguageSelect";
import { shopVar } from "@/lib/shopStyles";
import { useEvent as useShopEvent } from "@/providers/EventProvider";
import { useEvent as usePreRegistrationEvent } from "@/providers/PreRegistrationEventProvider";
import { useShopWhenAvailable } from "@/providers/ShopProvider";
import { RiCloseLine } from "@remixicon/react";
import { Event, ShopStyle } from "@/lib/TicketApi";
import { PreRegistrationEvent } from "@/lib/pre-registration-api";

/**
 * `imageFit` picks how the header image fills the band. The shop keeps `cover`
 * (photos, which should bleed edge to edge); the pre-registration page passes
 * `contain` because its header is usually the organizer's logo from the theme,
 * and cropping a logo to a 7rem band cuts the wordmark in half.
 */
export const Header: React.FC<{shopStyle?: ShopStyle; imageFit?: 'cover' | 'contain'}> = ({shopStyle, imageFit = 'cover'}) => {
    const { 
        isWidget, 
        shopSlug, 
        shop, 
    } = useShopWhenAvailable() ?? { 
        isWidget: false, 
        shopSlug: '', 
        shop: null,
    };

    let event: Event | PreRegistrationEvent | null = null;
    try {
        // Try to use pre-registration event context first
        const preRegContext = usePreRegistrationEvent();
        event = preRegContext.event;
    } catch {
        try {
            // Fall back to shop event context
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const shopContext = useShopEvent();
            event = shopContext.event;
        } catch {
            // If neither context is available, event will remain undefined
        }
    }

    const handleClose = () => {
        window.dispatchEvent(new CustomEvent('shop-close', { detail: { shopSlug } }));
    };

    const showLocation = !shopStyle?.hideLocation && event?.location?.name;
    const showDate = !shopStyle?.hideDate;

    // Safe way to display event dates
    const getEventDates = () => {
        try {
            return event ? displayEventDates(event, 'nl') : '';
        } catch {
            return '';
        }
    };

    const headerImageUrl = event?.headerImage || shop?.events?.[0]?.headerImage;

    return (
        <header>
            <div
                className={`relative ${imageFit === 'contain' ? 'bg-contain' : 'bg-cover'} bg-center bg-no-repeat w-full ${headerImageUrl ? 'h-28 p-4' : ''} ${isWidget ? 'sm:rounded-md sm:rounded-b-none' : 'sm:rounded-3xl sm:rounded-b-none'}`}
                style={headerImageUrl ? { backgroundImage: `url(${headerImageUrl})` } : {}}
            >
                {isWidget && headerImageUrl && (
                    <div
                        className="absolute top-6 right-6 z-20 p-2 hover:cursor-pointer hover:bg-gray-100/10 rounded-full hover:scale-105 transition-transform duration-200"
                        onClick={handleClose}
                    >
                        <RiCloseLine size={30} color={'black'} />
                    </div>
                )}
            </div>
            <div
                className={`px-8 py-4 flex items-center rounded-md justify-between ${headerImageUrl ? 'rounded-t-none' : 'rounded-t-md'}`}
                style={{ backgroundColor: shopVar('--shop-panelBackgroundColor') }}
            >
                <div>
                    <div className='text-xl font-bold'>{event?.name || 'Events'}</div>
                    {event && (
                        <div>
                            {showLocation && showDate && `${event.location?.name} | ${getEventDates()}`}
                            {showLocation && !showDate && event.location?.name}
                            {!showLocation && showDate && getEventDates()}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <LanguageSelect />
                    {isWidget && !headerImageUrl && (
                        <div
                            className={`p-2 hover:cursor-pointer hover:bg-gray-100/10 rounded-full hover:scale-105 transition-transform duration-200 bg-white/80 shadow`}
                            onClick={handleClose}
                        >
                            <RiCloseLine size={30} color={'black'} />
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};